import {IntlFormatters} from 'react-intl'
import * as Yup from 'yup'

type FormatMessage = IntlFormatters['formatMessage']

export function createLoginSchema(formatMessage: FormatMessage) {
  return Yup.object().shape({
    username: Yup.string()
      .min(3, formatMessage({id: 'auth.validation.username.min'}))
      .max(50, formatMessage({id: 'auth.validation.username.max'}))
      .required(formatMessage({id: 'auth.validation.username.required'})),
    password: Yup.string()
      .min(3, formatMessage({id: 'auth.validation.password.min'}))
      .max(50, formatMessage({id: 'auth.validation.password.max'}))
      .required(formatMessage({id: 'auth.validation.password.required'})),
  })
}

export function createForgotPasswordSchema(formatMessage: FormatMessage) {
  return Yup.object().shape({
    email: Yup.string()
      .email(formatMessage({id: 'auth.validation.email.format'}))
      .min(3, formatMessage({id: 'auth.validation.email.min'}))
      .max(50, formatMessage({id: 'auth.validation.email.max'}))
      .required(formatMessage({id: 'auth.validation.email.required'})),
  })
}

/** Email request flow (`forgot-password/ForgotPasswordEmailForm`). */
export function createForgotPasswordRequestSchema(formatMessage: FormatMessage) {
  return Yup.object().shape({
    email: Yup.string()
      .min(3, formatMessage({id: 'auth.validation.email.min'}))
      .max(50, formatMessage({id: 'auth.validation.email.max'}))
      .email(formatMessage({id: 'auth.validation.email.invalid'}))
      .required(formatMessage({id: 'auth.validation.email.required'})),
  })
}

export function createForgotPasswordResetSchema(formatMessage: FormatMessage) {
  const policy = formatMessage({id: 'auth.validation.reset.passwordPolicy'})

  return Yup.object().shape({
    newPassword: Yup.string()
      .required(formatMessage({id: 'auth.validation.reset.newPassword.required'}))
      .matches(/^(?=.*[a-zA-Z])(?=.*\d).{5,}$/, policy),
    verifyPassword: Yup.string()
      .oneOf(
        [Yup.ref('newPassword')],
        formatMessage({id: 'auth.validation.reset.passwordsMustMatch'})
      )
      .required(formatMessage({id: 'auth.validation.reset.verifyPassword.required'}))
      .matches(/^(?=.*[a-zA-Z])(?=.*\d).{5,}$/, policy),
  })
}

export function createOtpSchema(formatMessage: FormatMessage) {
  return Yup.object().shape({
    otp: Yup.string()
      .min(1, formatMessage({id: 'auth.validation.otp.min'}))
      .max(24, formatMessage({id: 'auth.validation.otp.max'}))
      .required(formatMessage({id: 'auth.validation.otp.required'})),
  })
}

export function createRegistrationSchema(formatMessage: FormatMessage) {
  return Yup.object().shape({
    name: Yup.string()
      .min(3, formatMessage({id: 'auth.validation.name.min'}))
      .max(100, formatMessage({id: 'auth.validation.name.max'}))
      .required(formatMessage({id: 'auth.validation.name.required'})),
    email: Yup.string()
      .email(formatMessage({id: 'auth.validation.email.format'}))
      .min(3, formatMessage({id: 'auth.validation.email.min'}))
      .max(100, formatMessage({id: 'auth.validation.email.max100'}))
      .required(formatMessage({id: 'auth.validation.email.required'})),
    nrp: Yup.string()
      .min(3, formatMessage({id: 'auth.validation.nrp.min'}))
      .max(25, formatMessage({id: 'auth.validation.nrp.max'}))
      .required(formatMessage({id: 'auth.validation.nrp.required'})),
    nik: Yup.string()
      .matches(/^[0-9]+$/, formatMessage({id: 'auth.validation.nik.digits'}))
      .length(16, formatMessage({id: 'auth.validation.nik.length'}))
      .required(formatMessage({id: 'auth.validation.nik.required'})),
    satkerId: Yup.number().required(formatMessage({id: 'auth.validation.satker.required'})),
    officerRankId: Yup.number().required(
      formatMessage({id: 'auth.validation.officerRank.required'})
    ),
    password: Yup.string()
      .min(8, formatMessage({id: 'auth.validation.password.min8'}))
      .max(50, formatMessage({id: 'auth.validation.password.max'}))
      .required(formatMessage({id: 'auth.validation.password.required'})),
    changepassword: Yup.string()
      .min(8, formatMessage({id: 'auth.validation.password.min8'}))
      .max(100, formatMessage({id: 'auth.validation.password.max100'}))
      .required(formatMessage({id: 'auth.validation.passwordConfirm.required'}))
      .oneOf(
        [Yup.ref('password')],
        formatMessage({id: 'auth.validation.passwordConfirm.mismatch'})
      ),
  })
}
